import type { ObjectId } from "@novorender/data-js-api";

/**
 * Turning a batch of ids into the other id generation, for both index backends.
 *
 * The engine and the API each hold the mapping natively and each answer it *positionally*
 * — entry `i` for input `i`, `null` for an id they carry no object for. What neither will
 * do is vet the input, and this input is not trustworthy: these ids arrive through the
 * `Webviewer` API, where a host app passes whatever it holds. A served index validates the
 * whole array and answers `422` for one malformed entry, so a single bad id would cost the
 * mapping for every other id in the request; a local one silently *coerces* —
 * `Uint32Array.from` turns `1.5` into `1`, which is very likely a real object and so the
 * wrong answer rather than no answer.
 *
 * So the ids that cannot be read are never sent, and answer `null` in place — which is
 * what either backend says for an id no object carries anyway.
 */

/** A `Procore/InstanceId` as an index will read one: 32 hex characters, either case. */
const INSTANCE_ID = /^[0-9a-f]{32}$/i;

/**
 * Whether `id` can be an instance id at all.
 *
 * The legacy `Procore/Id` cannot — it is shorter and not hex — which is what makes this a
 * filter rather than an assertion: `Webviewer.publicIdsToObjectIds` asks about both
 * generations in one list, and the legacy one is found by property instead.
 */
export function isInstanceId(id: string): boolean {
    return INSTANCE_ID.test(id);
}

/** Whether `id` can be an internal object id at all — that is, whether it is a `u32`. */
export function isInternalObjectId(id: ObjectId): boolean {
    return Number.isInteger(id) && id >= 0 && id <= 0xffff_ffff;
}

/**
 * `translate` applied to the ids it can read, with its answers walked back onto `ids`.
 *
 * `translate` sees a dense array of readable ids and answers aligned with *that*, so the
 * realignment here is what keeps the method's own contract: one entry per id the caller
 * asked about, in the caller's order, repeats answered once each. `undefined` from
 * `translate` — no mapping available, or an aborted read — passes straight through.
 *
 * An answer that is not one entry per id sent is `undefined` too. Both endpoints promise
 * that width, so a short one means the mapping cannot be read positionally at all: padding
 * the tail would answer `null` — "no such object" — for ids nobody actually answered for,
 * and the caller has no way to see the difference. `undefined` sends it to the property
 * query instead, which is the answer it already has for a mapping it cannot get.
 */
export async function translateIds<T, R>(
    ids: T[],
    isReadable: (id: T) => boolean,
    translate: (readable: T[]) => Promise<(R | null)[] | undefined>,
): Promise<(R | null)[] | undefined> {
    // Computed once rather than per walk: a broad selection makes this hundreds of
    // thousands of ids, and the predicate is a regex on one of the two paths.
    const readable = ids.map(isReadable);
    const sending = ids.filter((_, at) => readable[at]);

    const translated = await translate(sending);
    if (!translated || translated.length !== sending.length) {
        return undefined;
    }

    let at = 0;
    return readable.map((wasSent) => (wasSent ? translated[at++] : null));
}
